<template>
  <FloatingDropdown
    class="favourites-dropdown"
    :open="isOpen"
    @open="open"
    @close="close"
    @mouseover.native="mouseover"
    @mouseleave.native="mouseleave"
    :loading="favouritesDataLoading"
    ref="floatingdropdown"
  >
    <template #toggle>
      <slot>
        <div class="favourites-dropdown__default-toggle hide-text-for-small">
          <div class="favourites-dropdown__default-toggle-icon">
           <ApplicationIcon />
            <div
              v-if="favouritesRental.length"
              class="favourites-dropdown__default-toggle-count favourites-dropdown__default-toggle-count--rental"
              :class="(favouritesRental.length > 0 && favouritesASO.length > 0) ? 'favourites-dropdown__default-toggle-count---twoBallsVisible' : ''"
            >
              {{favouritesRental.length}}
            </div>
            <div
              v-if="favouritesASO.length"
              class="favourites-dropdown__default-toggle-count favourites-dropdown__default-toggle-count--ASO"
              :class="(favouritesRental.length > 0 && favouritesASO.length > 0) ? 'favourites-dropdown__default-toggle-count---twoBallsVisible' : ''"
            >
              {{favouritesASO.length}}
            </div>
          </div>
          {{empty ? translate('Asuntohakemus') : translate('Oma hakemus')}}
        </div>
      </slot>
    </template>

    <LoadSpinner v-if="favouritesDataLoading"/>
    <div
      v-else-if="empty"
      class="small-modal"
    >
      <div class="small-modal__title">
        {{translate('Et ole vielä lisännyt asuntoja hakemukselle.')}}
      </div>
      <div class="favourites-dropdown__empty-instructions-title">
        {{translate('Voit tehdä sen asuntohaussamme:')}}
      </div>
      <ol class="favourites-dropdown__empty-instructions">
        <li>{{translate('Hae asuntoja')}}</li>
        <li>{{translate('Lisää mieleisesi asunnot hakemukseen')}}</li>
        <li>{{translate('Täytä ja lähetä asuntohakemus')}}</li>
      </ol>

      <div class="small-modal__cta">
        <a
          :href="urlApartmentSearch"
          class="btn btn--large"
        >
          {{translate('Hae asuntoja')}}
        </a>
      </div>
    </div>
    <template v-else>

      <!-- Lists -->
      <div
        v-for="list in listsComputed"
        class="favourites-dropdown__list"
      >

        <!-- Apartments -->
        <div :class="['favourites-dropdown__apartments', `favourites-dropdown__apartments--${list.color}`]">
          <FavouriteCard
            v-for="apartment in listDisplayedApartments(list)"
            :key="apartment.mpc_asuntoid"
            :url="apartment.available ? apartmentUrl(apartment) : null"
            :color="list.color"
            :color-diamond="apartment.available ? null : 'black'"
            @remove="store.removeFavourite({uuid: apartment.mpc_asuntoid, type: apartment.type})"
          >
            <template #title>
              {{apartment.mpc_huoneistotyyppi}}<br>
              {{apartment.mpc_name}}
            </template>

            <template #sub-title>
              {{apartmentDisplayLocation(apartment)}}
            </template>

            <template #side>
              <div
                v-if="apartment.available"
                class="favourites-dropdown__apartment-price stacked-spec"
              >
                <div class="stacked-spec__title">
                  {{translate(apartmentDisplayPriceTitle(apartment))}}
                </div>
                <div class="stacked-spec__value">
                  {{apartmentDisplayPrice(apartment)}}
                </div>
              </div>
            </template>
          </FavouriteCard>
        </div>

        <!-- Expand -->
        <button
          v-show="list.apartments.length > 2 && !list.expanded"
          class="favourites-dropdown__expand default-cursor"
          @click="lists[list.type].expanded = false"
        >

          <span class="favourites-dropdown__expand-title">
              {{sprintf(translate(list.moreText), list.apartments.length - 1)}}
            </span>
          <span class="favourites-dropdown__expand-subtitle">
              {{moreCities(list.apartments)}}
            </span>
        </button>

        <!-- CTA -->
        <div class="favourites-dropdown__cta" :test="list.type" v-if="list.type !== 'owner-occupancy'">
          <a
            :href="list.ctaLink"
            :class="['btn', 'btn--fluid']"
            :id="`favourites-link-${list.applicationType}-4001`"
          >
            {{translate(list.ctaText)}}
          </a>
        </div>

      </div>

    </template>
  </FloatingDropdown>
</template>

<script>
  import {sprintf} from 'sprintf-js';
  import { useFavouritesStore } from './store/index';

  import globals from '../lib/globals';
  import utils from './mixins/utils';
  import apartmentDisplayFields from './mixins/apartmentDisplayFields';
  import LoadSpinner from './components/LoadSpinner';
  import FavouriteCard from './components/FavouriteCard';
  import FloatingDropdown from './components/FloatingDropdown';
  import applicationReset from '../lib/applicationReset';

  import ApplicationIcon from './icons/ApplicationIcon.vue'
  import ChevronIcon from './icons/ChevronDownIcon.vue'
  import ArrowIcon from './icons/ArrowIcon.vue';
  import PlusIcon from './icons/PlusIcon.vue';

  export default {
    components: {
      FloatingDropdown,
      LoadSpinner,
      FavouriteCard,
      ApplicationIcon,
      ChevronIcon,
      ArrowIcon,
      PlusIcon
    },
    mixins: [
      apartmentDisplayFields,
      utils.translate,
      utils.apartmentUrl,
    ],
    data() {
      return {
        urlApartmentSearch: globals.urlApartmentSearch,
        isOpen: false,
        listOrder: [
          'right-of-occupancy',
          'rental',
          'owner-occupancy'
        ],
        lists: {
          'right-of-occupancy': {
            expanded: false,
            moreText: '%s muuta ASO-asuntoa',
            ctaTextNew: 'Täytä ASO-hakemus',
            ctaTextReturn: 'Palaa ASO-hakemukselle',
            ctaLink: globals.urlApplicationRightOfOccupancy,
            color: 'green',
            applicationType: 'aso',
          },
          rental: {
            expanded: false,
            moreText: '%s muuta vuokra-asuntoa',
            ctaTextNew: 'Täytä vuokrahakemus',
            ctaTextReturn: 'Palaa vuokrahakemukselle',
            ctaLink: globals.urlApplicationRent,
            color: 'green',
            applicationType: 'rent',
          },
          'owner-occupancy': {
            expanded: false,
            moreText: '%s muuta omistusasuntoa',
            ctaTextNew: 'Täytä omistusasuntovaraus',
            ctaTextReturn: 'Palaa omistusasuntovaraukselle',
            ctaLink: globals.urlApplicationOwnerOccupied,
            color: 'green',
            applicationType: 'owner-occupancy',
          }
        },
        isExternal: false,
      }
    },
    computed: {
      store() {
        return useFavouritesStore();
      },
      favouritesAll() {
        return this.store.favouritesAll;
      },
      favouritesRental() {
        return this.store.favouritesRental;
      },
      favouritesASO() {
        return this.store.favouritesASO;
      },
      getFavouritesMenuOpenExternal() {
        return this.store.getFavouritesMenuOpenExternal;
      },
      favouritesDataLoading() {
        return this.store.favouritesDataLoading;
      },
      listsComputed() {
        const store = this.store;
        const listsWithApartments = Object.assign({}, ...Object.entries(this.lists).map(([type, list]) => {
          return {
            [type]: {
              ...list,
              type,
              apartments: type === 'owner-occupancy' ? [] : store.favouritesData[type],
              ctaText: store.localSave[type] && type !== 'owner-occupancy' ? list.ctaTextReturn : list.ctaTextNew,
              show: store.favouritesData[type].length > 0 && type !== 'owner-occupancy' || store.localSave[type],
            }
          }
        }));
        return this.listOrder
          .map(listName => listsWithApartments[listName])
          .filter(list => list.show);
      },
      empty() {
        return this.favouritesRental.length === 0 && this.favouritesASO.length === 0;
      },
      cp() {
        return this.favouritesAll.length;
      }
    },
    mounted() {
      this.continuePolling = false;
    },
    beforeUnmount() {
      this.continuePolling = false;
    },
    methods: {
      sprintf,
      mouseover() {
        this.stopTimer();
      },
      mouseleave() {
        this.startTimer();
      },
      open() {
        this.isOpen = true;
        this.store.requestAndCleanUpFavourites();
      },
      close() {
        this.isOpen = false;
        this.isExternal = false;
        this.stopTimer();
        Object.entries(this.lists)
          .forEach(([, list]) => {
            list.expanded = false;
          })
        this.store.toggleFavouritesMenuExternal(false);
      },
      listDisplayedApartments(list) {
        if (list.apartments.length === 0) {
          return [];
        }
        if (list.apartments.length < 3) {
          return list.apartments;
        }
        return list.expanded
          ? list.apartments
          : list.apartments.slice(0, 1);
      },
      moreCities(apartments) {
        const cities = apartments
          .slice(1)
          .map(apartment => apartment.city.mpc_name);
        const citiesUnique = [...new Set(cities)];

        return citiesUnique.join(', ');
      },
      startTimer() {
        this.timer = setTimeout(() => {
          this.close();
        }, 10000);
      },
      stopTimer() {
        clearTimeout(this.timer);
      },
    },
    watch: {
      cp: function(newValue, oldValue) {
       // console.log('was: ', oldValue, ' now: ', newValue)
      },
      favouritesAll() {
        if (this.isOpen) {
          this.store.requestAndCleanUpFavourites();
        }
      },
      getFavouritesMenuOpenExternal() {
        if(this.getFavouritesMenuOpenExternal) {
          this.open();
          this.isExternal = true;
        }
      },
      favouritesDataLoading() {
        if(!this.favouritesDataLoading) {
          if(this.isExternal) {
            this.startTimer();
          }
        }
      }
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  .favourites-dropdown {
  }

  .favourites-dropdown__default-toggle {
    font-size: r(16);
    line-height: r(20);
    font-family: $ff-lato;
    display: flex;
    align-items: center;
    color: $color-green-2025;
    gap: 0.35rem;
  }

  .favourites-dropdown__default-toggle-icon {
    position: relative;
    width: 1.5rem;
    height: 1.5rem;
    margin-top: -0.125rem;
    margin-bottom: -0.125rem;
    display: grid;
    align-content: center;
  }

  .favourites-dropdown__default-toggle-count {
    @include font-body-10();
    font-weight: 700;
    position: absolute;
    display: flex;
    top: -0.25rem;
    right: -0.25rem;
    width: r(17);
    height: r(17);
    align-items: center;
    justify-content: center;
    background-color: $color-pink-2025;
    border-radius: 50%;
    color: $color-green-2025;

    &.favourites-dropdown__default-toggle-count--rental {
      background-color: $color-green-2025;
      color: $color-white;
      font-weight: bold;
    }

    &.favourites-dropdown__default-toggle-count---twoBallsVisible {
      &.favourites-dropdown__default-toggle-count--rental {
        top: r(4);
      }

      &.favourites-dropdown__default-toggle-count--ASO {
        top: r(22);
      }
    }
  }

  .favourites-dropdown__empty-instructions-title {
    @include font-label-16;
    margin-bottom: r(8);
  }

  .favourites-dropdown__empty-instructions {
    padding-left: r(16);
    margin: 0;

    li {
      margin-bottom: .5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .favourites-dropdown__list {
    & + & {
      border-top: r(3) solid $color-gray;
    }
  }

  .favourites-dropdown__apartments {
    &--pink {
      background-color: $color-pink-light-2025;
    }

    &--yellow {
      background-color: #fef0dc;
    }
  }

  .favourites-dropdown__apartment-price {
    margin-bottom: r(8);
  }

  .favourites-dropdown__expand {
    @include reset-button;
    position: relative;
    padding: r(16) r(20) r(24) r(54);
    width: 100%;
    background-color: #faf9f6;
  }
  .default-cursor {
    cursor: default;
  }

  .favourites-dropdown__expand-icon {
    position: absolute;
    top: r(10);
    left: r(10);
  }

  .favourites-dropdown__expand-title {
    @include font-heavy-16();
    display: block;
  }

  .favourites-dropdown__expand-subtitle {
    @include font-body-12();
    display: block;
  }

  .favourites-dropdown__cta {
    padding: r(16) r(48);
  }
</style>
